import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useToast } from '../contexts/ToastContext'

interface ProviderOnboardingInviteProps {
  merchantId?: string
  onClose?: () => void
}

const ProviderOnboardingInvite: React.FC<ProviderOnboardingInviteProps> = ({ merchantId, onClose }) => {
  const toast = useToast()
  const [label, setLabel] = useState('')
  const [expiryHours, setExpiryHours] = useState(168) // 7 days
  const [creating, setCreating] = useState(false)
  const [inviteLink, setInviteLink] = useState('')

  const handleCreate = async () => {
    setCreating(true)
    setInviteLink('')

    try {
      const { data, error: invokeErr } = await supabase.functions.invoke(
        'provider-onboarding-token-create',
        {
          body: {
            merchant_id: merchantId || undefined,
            label: label.trim() || undefined,
            expires_in_hours: expiryHours,
          },
        }
      )

      if (invokeErr) throw invokeErr
      const token = (data as any)?.token
      if (!token) throw new Error('No token received')

      const url = `${window.location.origin}/provider/onboarding?token=${encodeURIComponent(token)}`
      setInviteLink(url)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create invite token')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="flex flex-col">
          <label className="text-xs text-white/50 mb-1">Label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g., For Acme Warehouse"
            className="border border-white/15 text-white text-sm px-3 py-2 bg-transparent"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-white/50 mb-1">Expiry</label>
          <select
            className="border border-white/15 text-white text-sm px-3 py-2 bg-transparent"
            value={String(expiryHours)}
            onChange={(e) => setExpiryHours(Number(e.target.value))}
          >
            <option value="24">1 day</option>
            <option value="72">3 days</option>
            <option value="168">7 days</option>
            <option value="720">30 days</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-4 py-2 bg-orange-500 text-white border-2 border-orange-500 hover:bg-orange-400 rounded-xl transition-colors disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Generate Invite'}
          </button>
        </div>
      </div>

      {inviteLink && (
        <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
          <label className="text-xs text-emerald-400 mb-2 block">
            Provider Onboarding Invite Link (one-time use)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={inviteLink}
              className="flex-1 px-3 py-2 bg-transparent border border-emerald-500/30 text-white text-xs"
            />
            <button
              onClick={() => navigator.clipboard.writeText(inviteLink)}
              className="px-3 py-2 border border-emerald-500/30 hover:bg-white/[0.08] text-sm text-emerald-400"
            >
              Copy
            </button>
          </div>
          <p className="mt-2 text-xs text-emerald-400">
            Share this link with the provider. They will need to use it to complete their onboarding.
          </p>
        </div>
      )}

      {onClose && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white/50 hover:text-white text-sm"
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}

export default ProviderOnboardingInvite
